import {axiosWrapper} from 'Common/services/AxiosWrapper';
import {IListResponse, IObjectResponse} from 'Common/models/IResponse';
import {IUserHorse, IUserHorseOrder, IHorseReport} from 'Horse/models/IUserHorse';
import {IServerHorse, IServerUserHorse, IServerUserHorseOrder} from './types/horse';
import {convertHorseToRequest, convertServerUserHorse} from './converters/horse';
import {Gender} from 'Common/constants/Gender';
import IFoundHorse from 'Horse/models/IFoundHorse';
import {IHorseRequestParams} from 'Horse/models/IHorseRequest';
import {IHorseProfileTypeRequest} from 'Horse/store/horse';
import {withBirthDate} from 'Common/helpers/withBirthDate';
import {IShortAbility} from 'Dictionaries/models/IAbilities';
import {ProfileType} from 'Common/constants/ProfileType';
import {Nullable} from 'Common/types';
import {convertServerHorseLastOrderToClient} from 'Horse/services/converters/horseLastOrder';
import {IHorseProfile} from 'Horse/models/IHorseProfile';
import {isAdminVisitor} from 'Common/helpers/isAdminVisitor';
import {IRequestParams} from 'Common/models/IRequestParams';
import {getApiByVisitorType} from 'Common/helpers/getApiByVisitorType';
import {VisitorType} from 'Common/constants/VisitorType';
import {IHorseOwner} from 'Horse/models/IHorseOwner';
import {IOwnerHorse} from 'Shared/models/IOwnerHorse';
import {withAvatar} from 'Common/helpers/withAvatar';
import {IServerHorseOwner} from './types/owner';
import {withUserName} from 'Common/helpers/withUserName';

const getHorses = async (params: IRequestParams, userId: number): Promise<IListResponse<IUserHorse>> => {
  const response = await axiosWrapper.get<IListResponse<IServerUserHorse>>(
    isAdminVisitor() ? `/UserProfileHorsesAdmin` : `/UserProfileHorses`,
    {
      params: {...params, userId},
    }
  );
  return {...response.data, data: response.data.data.map(convertServerUserHorse)};
};

const getHorse = async (id: number): Promise<Nullable<IHorseProfile>> => {
  const response = await axiosWrapper.get<IObjectResponse<Nullable<IServerHorse>>>(
    (isAdminVisitor() ? `/HorseProfileAdmin` : `/Horses`) + `/${id}`
  );
  return response.data.data && withAvatar(withBirthDate(response.data.data));
};

const createHorse = async (params: IHorseRequestParams): Promise<number> => {
  const response = await axiosWrapper.post<IObjectResponse<number>>(`/HorsesCreate/`, convertHorseToRequest(params));
  return response.data.data;
};

const updateHorse = async (params: IHorseRequestParams): Promise<void> => {
  await axiosWrapper.put<IObjectResponse<IServerHorse>>(`/HorsesUpdate/`, convertHorseToRequest(params));
};

const deleteHorse = async (id: number): Promise<void> => {
  await axiosWrapper.delete<IObjectResponse<IServerHorse>>(`/HorsesArchive/${id}`);
};

const searchHorseByGenderAndName = async (name: string, gender?: Gender): Promise<IFoundHorse[]> => {
  const api = getApiByVisitorType({
    [VisitorType.User]: `/HorseListSearch`,
    [VisitorType.AssociationEmployee]: `/HorseListSearchAssociation`,
  });

  const response = await axiosWrapper.get<IObjectResponse<IFoundHorse[] | null>>(api, {params: {name, gender}});

  return response.data.data?.map(withBirthDate).map(withAvatar) || [];
};

const updateHorseProfileType = async (request: IHorseProfileTypeRequest): Promise<ProfileType> => {
  const response = await axiosWrapper.patch<IObjectResponse<ProfileType>>(`/HorseProfileTypes`, {
    horseId: request.horseId,
    profileType: request.profileType,
  });

  return response.data.data;
};

const updateHorseAncestryProfileType = async (request: IHorseProfileTypeRequest): Promise<ProfileType> => {
  const response = await axiosWrapper.patch<IObjectResponse<ProfileType>>(`/HorseAncestryProfileTypes`, {
    horseId: request.horseId,
    profileType: request.profileType,
  });

  return response.data.data;
};

const getHorseAbilities = async (horseId: number): Promise<IListResponse<IShortAbility>> => {
  const response = await axiosWrapper.get<IListResponse<IShortAbility>>(
    (isAdminVisitor() ? `/HorseAbilitiesAdmin` : `/HorseAbilities`) + `/${horseId}`
  );
  return response.data;
};

const getHorseParantageReport = async (horseId: number): Promise<IObjectResponse<IHorseReport>> => {
  const api = getApiByVisitorType({
    [VisitorType.User]: `/HorseParentageReport`,
    [VisitorType.Admin]: `/HorseParentageReportAdmin`,
  });

  const response = await axiosWrapper.get<IObjectResponse<IHorseReport>>(`${api}/${horseId}`);
  return response.data;
};

const getHorseLastOrder = async (horseId: number): Promise<IUserHorseOrder> => {
  const response = await axiosWrapper.get<IObjectResponse<IServerUserHorseOrder>>(
    (isAdminVisitor() ? `/HorseOrdersAdmin` : `/HorseOrders`) + `/Last/${horseId}`
  );
  return convertServerHorseLastOrderToClient(response.data.data);
};

const searchHorseById = async (horseId: number): Promise<IFoundHorse> => {
  const api = getApiByVisitorType({
    [VisitorType.User]: `/HorseListItemSearch`,
    [VisitorType.AssociationEmployee]: `/HorseListItemSearchAssociation`,
  });

  const response = await axiosWrapper.get<IObjectResponse<IFoundHorse>>(`${api}/${horseId}`);

  return withAvatar(withBirthDate(response.data.data));
};

const getHorseOwner = async (horseId: number): Promise<IHorseOwner> => {
  const api = getApiByVisitorType({
    [VisitorType.User]: `/HorseOwner`,
    [VisitorType.Admin]: `/HorseOwnerAdmin`,
  });

  const response = await axiosWrapper.get<IObjectResponse<IServerHorseOwner>>(`${api}/${horseId}`);
  return withUserName(response.data.data);
};

const getOwnerHorses = async (horseId: number): Promise<IListResponse<IOwnerHorse>> => {
  const api = getApiByVisitorType({
    [VisitorType.User]: `/HorseProfileHorses`,
    [VisitorType.Admin]: `/HorseProfileHorsesAdmin`,
  });

  const response = await axiosWrapper.get<IListResponse<IOwnerHorse>>(`${api}/${horseId}`);
  return {...response.data, data: response.data.data.map(withBirthDate)};
};

export default {
  getHorse,
  getHorses,
  createHorse,
  updateHorse,
  deleteHorse,
  searchHorseByGenderAndName,
  updateHorseProfileType,
  updateHorseAncestryProfileType,
  getHorseAbilities,
  getHorseParantageReport,
  getHorseLastOrder,
  searchHorseById,
  getHorseOwner,
  getOwnerHorses,
};
